import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "../AuthContext";
import apiConfig from "../apiConfig";

/**
 * useAuditLog — Global Audit Log Hook
 *
 * Usage:
 *   const { logAudit, getDiff } = useAuditLog();
 *
 *   // CREATE — pass full new object
 *   logAudit("CREATE", "SaleApi", 1001, null, { InvoiceNo: "INV-1001", Amount: 5000 });
 *
 *   // UPDATE — pass full old & new; only changed fields are saved automatically
 *   logAudit("UPDATE", "PurchaseApi", 205, oldValues, newValues);
 *
 *   // Or use getDiff manually if you need the diff object before calling logAudit
 *   const { oldDiff, newDiff } = getDiff(oldValues, newValues);
 *   logAudit("UPDATE", "PurchaseApi", 205, oldDiff, newDiff);
 *
 *   // DELETE — pass old object only
 *   logAudit("DELETE", "ProductApi", 45, { Name: "Item A" }, null);
 *
 *   // LOGIN / LOGOUT
 *   logAudit("LOGIN",  "LoginApi", 0);
 *   logAudit("LOGOUT", "LoginApi", 0);
 *
 * @param action     - "CREATE" | "UPDATE" | "DELETE" | "LOGIN" | "LOGOUT" | "VIEW" | "PRINT" | ...
 * @param tableName  - API/Table name  e.g. "SaleApi", "PurchaseApi"
 * @param recordId   - The ID of the record affected (0 for login/logout)
 * @param oldValues  - Object of old values before change (null for CREATE/LOGIN)
 * @param newValues  - Object of new values after change  (null for DELETE/LOGOUT)
 *
 * NOTE: For UPDATE actions, only the fields that actually changed are stored.
 *       Both oldValues and newValues will contain only the changed-field subset.
 */

export const AUDIT_ACTION = {
  LOGIN:   "LOGIN",
  LOGOUT:  "LOGOUT",
  CREATE:  "CREATE",
  UPDATE:  "UPDATE",
  DELETE:  "DELETE",
  VIEW:    "VIEW",
  PRINT:   "PRINT",
  EXPORT:  "EXPORT",
  VERIFY:  "VERIFY",
  APPROVE: "APPROVE",
};

/**
 * getDiff — standalone utility (also exported from the hook)
 *
 * Compares two plain objects and returns only the keys whose values differ.
 * Skips internal/system keys that are not meaningful to audit
 * (e.g. timestamps, modified-by fields).
 *
 * @returns {{ oldDiff: object|null, newDiff: object|null, hasChanges: boolean }}
 */
const SKIP_KEYS = new Set([
  "ModifiedDate", "ModifiedBy", "CreatedDate", "CreatedBy",
  "UpdatedDate",  "UpdatedBy",  "type",
]);

export const getDiff = (oldObj, newObj) => {
  if (!oldObj || !newObj || typeof oldObj !== "object" || typeof newObj !== "object") {
    return { oldDiff: oldObj, newDiff: newObj, hasChanges: true };
  }

  const allKeys = Array.from(
    new Set([...Object.keys(oldObj), ...Object.keys(newObj)])
  ).filter((k) => !SKIP_KEYS.has(k));

  const oldDiff = {};
  const newDiff = {};

  allKeys.forEach((key) => {
    const oldVal = oldObj[key];
    const newVal = newObj[key];

    // Normalise to string for a simple equality check
    // (handles null vs undefined vs "" edge cases)
    const oldStr = oldVal === null || oldVal === undefined ? "" : String(oldVal);
    const newStr = newVal === null || newVal === undefined ? "" : String(newVal);

    if (oldStr !== newStr) {
      oldDiff[key] = oldVal ?? null;
      newDiff[key] = newVal ?? null;
    }
  });

  const hasChanges = Object.keys(oldDiff).length > 0;
  return {
    oldDiff: hasChanges ? oldDiff : null,
    newDiff: hasChanges ? newDiff : null,
    hasChanges,
  };
};

const useAuditLog = () => {
  const { companyId, branchId, userId, username, token, roleName } = useAuth();

  // Cache the client IP so we only fetch it once per session
  const ipRef = useRef(null);

  useEffect(() => {
    if (ipRef.current) return; // already fetched
    fetch('https://api.ipify.org?format=json')
      .then(r => r.json())
      .then(data => { ipRef.current = data?.ip || null; })
      .catch(() => { ipRef.current = null; });
  }, []);

  const logAudit = useCallback(
    (action, tableName, recordId = 0, oldValues = null, newValues = null) => {
      if (!token && action !== AUDIT_ACTION.LOGIN) return;

      let finalOld = oldValues;
      let finalNew = newValues;

      // For UPDATE: automatically compute diff so only changed fields are stored
      if (action === AUDIT_ACTION.UPDATE && oldValues && newValues) {
        const { oldDiff, newDiff, hasChanges } = getDiff(oldValues, newValues);
        if (!hasChanges) {
          // Nothing actually changed — skip logging entirely
          return;
        }
        finalOld = oldDiff;
        finalNew = newDiff;
      }

      const payload = {
        CompanyId: companyId || 0,
        BranchId:  branchId  || 0,
        UserId:    userId    || 0,
        Username:  username  || "Unknown",
        RoleName:  roleName  || null,
        Action:    action,
        TableName: tableName || "",
        RecordId:  recordId  || 0,
        OldValues: finalOld ? JSON.stringify(finalOld) : null,
        NewValues: finalNew ? JSON.stringify(finalNew) : null,
        IpAddress:   ipRef.current || null,
        CreatedDate: new Date().toISOString(),
      };

      // Fire-and-forget — UI block ചെയ്യില്ല
      // Capture all values into local vars so the request survives component unmount
      const capturedToken   = token;
      const capturedPayload = { ...payload };

      // Use keepalive fetch so the request survives page navigation / component unmount
      try {
        fetch(`${apiConfig.apiUrl}AuditLogApi`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${capturedToken}`,
          },
          body: JSON.stringify(capturedPayload),
          keepalive: true,
        }).catch((err) => {
          console.warn("[AuditLog] keepalive fetch failed:", err?.message || err);
        });
      } catch (err) {
        console.warn("[AuditLog] Failed:", err?.message || err);
      }
    },
    [companyId, branchId, userId, username, token, roleName]
  );

  return { logAudit, getDiff };
};

export default useAuditLog;
